import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  ReportGroupBy,
  ReportPeriod,
  TReportPaymentsBySignupFormBreakdownData,
  TReportPaymentsBySignupFormBreakdownParams,
} from '@src/types/reports';
import { TOption } from '@src/types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import dayjs from 'dayjs';

import {
  exportPaymentsBySignupFormBreakdown,
  getInvoicesBySignupFormOptions,
  getPaymentsBySignupFormBreakdown,
} from '@src/services/reports';
import { downloadFile } from '@src/lib/utils';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.dataKey}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              ${entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const PaymentsBySignupFormBreakdown: FC = () => {
  const [data, setData] = useState<TReportPaymentsBySignupFormBreakdownData[]>([]);
  const [signupFormOptions, setSignupFormOptions] = useState<TOption[]>([]);

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableSignupForms: true,
    enableComparisons: true,
  });

  const { columns, displayItems, tableData } = useReportTableColumns({
    data,
    selectedItems: filters.selectedSignupForms,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.signup_forms[key] || 0,
    getPreviousPeriodField: (item, key) => item.signup_forms_previous?.[key],
    getPreviousYearField: (item, key) => item.signup_forms_previous_year?.[key],
    itemsField: 'signup_forms',
    valueFormatter: (_, value: number) => `$${value.toLocaleString()}`,
    includeTotalsRow: true,
  });

  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.signup_forms[key] || 0,
    getPreviousPeriodField: (item, key) => item.signup_forms_previous?.[key],
    getPreviousYearField: (item, key) => item.signup_forms_previous_year?.[key],
  });

  const buildReportPayload = (): TReportPaymentsBySignupFormBreakdownParams => {
    const savedFormIds = filters.selectedSignupForms
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));

    return {
      start_date: filters.startDate
        ? dayjs(filters.startDate).format('YYYY-MM-DD')
        : null,
      end_date: filters.endDate
        ? dayjs(filters.endDate).format('YYYY-MM-DD')
        : null,
      group: filters.selectedGroupBy.toLowerCase() as ReportGroupBy,
      saved_form_ids: savedFormIds.length ? savedFormIds : undefined,
      compare_to_previous_period: filters.compareToPreviousPeriod,
      compare_to_same_period_previous_year:
        filters.compareToSamePeriodPreviousYear,
    };
  };

  const fetchSignupFormOptions = async () => {
    try {
      const forms = await getInvoicesBySignupFormOptions();

      setSignupFormOptions(
        forms.map((item) => ({
          label: item.title,
          value: String(item.saved_form_id),
        }))
      );
    } catch (error) {
      console.error('Error fetching signup form options:', error);
      toast.error('Failed to load signup form options');
    }
  };

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildReportPayload();
      const response = await getPaymentsBySignupFormBreakdown(payload);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const downloadCsvFile = (blobPart: BlobPart, fileName: string) => {
    const csvBlob = new Blob([blobPart], { type: 'text/csv;charset=utf-8;' });
    const csvUrl = URL.createObjectURL(csvBlob);
    const link = document.createElement('a');

    link.href = csvUrl;
    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(csvUrl);
    document.body.removeChild(link);
  };

  const handleExportReport = async (format: 'csv' | 'xlsx') => {
    try {
      const payload = buildReportPayload();
      const response = await exportPaymentsBySignupFormBreakdown(payload, format);

      if (format === 'csv') {
        downloadCsvFile(response.data, 'payments_by_signup_form_breakdown');
      } else {
        downloadFile(response.data, 'payments_by_signup_form_breakdown');
      }

      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  useEffect(() => {
    fetchSignupFormOptions();
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Payments by Signup Form Breakdown
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          selectedSignupForms={filters.selectedSignupForms}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          showSignupFormsFilter={true}
          signupFormOptions={signupFormOptions}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
          onSignupFormsChange={filters.setSelectedSignupForms}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              {displayItems.map((signupForm, index) => (
                <Line
                  key={signupForm}
                  activeDot={{ r: 6 }}
                  dataKey={signupForm}
                  dot={{ fill: getChartColor(index), r: 4 }}
                  name={signupForm}
                  stroke={getChartColor(index)}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}

              {filters.compareToPreviousPeriod &&
                displayItems.map((signupForm, index) => (
                  <Line
                    key={`${signupForm}_previous_period`}
                    activeDot={{ r: 4 }}
                    dataKey={`${signupForm}_previous_period`}
                    dot={{
                      fill: getChartColor(index),
                      r: 3,
                    }}
                    name={`${signupForm} (Previous Period)`}
                    stroke={getChartColor(index)}
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}

              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((signupForm, index) => (
                  <Line
                    key={`${signupForm}_previous_year`}
                    activeDot={{ r: 4 }}
                    dataKey={`${signupForm}_previous_year`}
                    dot={{
                      fill: getChartColor(index),
                      r: 3,
                    }}
                    name={`${signupForm} (Previous Year)`}
                    stroke={getChartColor(index)}
                    strokeDasharray="2 2"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex flex-wrap justify-end gap-3">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={() => handleExportReport('csv')}
            >
              Download CSV
            </Button>
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={() => handleExportReport('xlsx')}
            >
              Download XLSX
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
